import { HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import {
  FileItem,
  InspectionDto,
  InspectionModel,
  Order,
  Stats
} from '@authority-labs/api-interfaces';
import { Path } from '@authority-labs/enums';
import { GetManyDefaultResponse } from '@nestjsx/crud';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrudService } from '../../shared/services/crud/crud.service';

@Injectable({
  providedIn: 'root'
})
export class OrdersService extends CrudService<Order> {
  constructor(injector: Injector) {
    super(injector, Path.Orders, Order);
  }

  getInspection(orderId: number): Observable<InspectionModel> {
    return this.http
      .get<InspectionDto>(`${this.api}/${orderId}/inspection`)
      .pipe(map((_inspection) => new InspectionModel(_inspection)));
  }

  getPlans(orderId: number): Observable<FileItem[]> {
    return this.http.get<FileItem[]>(`${this.api}/${orderId}/plans`);
  }

  globalSearch(
    value: string,
    page: number = 1
  ): Observable<GetManyDefaultResponse<Order>> {
    return this.search({
      value,
      page,
      searchFields: ['id', 'subProjectId', 'procurementNumber'],
      selectFields: [
        'id',
        'name',
        'address',
        'subProjectId',
        'procurementNumber'
      ]
    });
  }

  createIcountOrder(orderId: number): Observable<Order> {
    return this.http.post<Order>(`${this.api}/${orderId}/icount/orders`, {});
  }
  /** Deletes the order's certificate; the server resolves the file id itself. */
  deleteCertificate(orderId: number): Observable<void> {
    return this.http.delete<void>(`${this.api}/${orderId}/certificate`);
  }

  createPDF(
    orderId: number | string,
    version: number = 0,
    waitForFunc?: string
  ): Observable<FileItem> {
    return this.http.post<FileItem>(`${this.api}/certificate/invoice`, {
      orderId,
      version,
      waitForFunc
    });
  }

  getOrderStats(dateFrom?: Date, dateTo?: Date): Observable<Stats> {
    let params: HttpParams = new HttpParams();
    params = this.getDateTSParam(dateFrom, 'dateFromTs', params);
    params = this.getDateTSParam(dateTo, 'dateToTs', params);

    return this.http.get<Stats>(`${this.api}/orderStats`, { params });
  }

  getContent(orderId: number): Observable<string> {
    return this.http.get<string>(`${this.api}/${orderId}/certificate.html`, {
      observe: 'body',
      responseType: 'text' as 'json'
    });
  }

  manyPlainToClass(orders: Order[]): Order[] {
    orders = orders.map(this.setLastNote);
    return plainToInstance(Order, orders);
  }

  plainToClass = (order: Order): Order =>
    plainToInstance(Order, this.setLastNote(order));

  private setLastNote(order: Order): Order {
    order.lastNote =
      order?.notes?.length > 0
        ? order.notes[order.notes.length - 1].content
        : '';

    return order;
  }

  private getDateTSParam(
    date: Date,
    name: string,
    params: HttpParams
  ): HttpParams {
    if (date) {
      params = params.set(name, String(date.getTime()));
    }
    return params;
  }
}
